import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Player } from '../services/api';

const PlayerDetail: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlayer = async () => {
      if (!playerId) return;
      
      try {
        setLoading(true);
        const playerData = await apiService.getPlayer(playerId);
        setPlayer(playerData);
      } catch (err) {
        console.error('Error fetching player:', err);
        setError('Failed to load player data');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [playerId]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '18px'
      }}>
        Loading player information...
      </div>
    );
  }

  if (error || !player) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '18px',
        color: 'red'
      }}>
        {error || 'Player not found'}
      </div>
    );
  }

  // Sort stats by match date ascending for the game log
  const sortedStats = (player.stats || []).slice().sort((a: any, b: any) => {
    const dateA = a?.match?.date ? new Date(a.match.date).getTime() : 0;
    const dateB = b?.match?.date ? new Date(b.match.date).getTime() : 0;
    return dateA - dateB;
  });

  return (
    <div className="modern-container">
      <div className="modern-page-card">
        {/* Player Header Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap'
        }}>
          {player.photo ? (
            <img 
              src={player.photo} 
              alt={player.name} 
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid #1976d2',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
              }} 
            />
          ) : (
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: '#f8fafc',
              border: '3px solid #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              fontWeight: 700,
              color: '#64748b',
              boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
            }}>
              {player.name.charAt(0)}
            </div>
          )}
          
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>{player.name}</h1>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 16px' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#64748b' }}>
                <strong style={{ color: '#334155' }}>Team:</strong> <Link 
                  to={`/summer-league/${player.year}/${player.group}/teams/${player.team.id}`}
                  className="modern-link"
                >
                  {player.team.name}
                </Link>
              </p>
              <p style={{ margin: 0, fontSize: '15px', color: '#64748b' }}>
                <strong style={{ color: '#334155' }}>Position:</strong> {player.position || 'N/A'}
              </p>
              <p style={{ margin: 0, fontSize: '15px', color: '#64748b' }}>
                <strong style={{ color: '#334155' }}>Jersey:</strong> #{player.jersey !== null && player.jersey !== undefined ? player.jersey : 'N/A'}
              </p>
              {player.height && (
                <p style={{ margin: 0, fontSize: '15px', color: '#64748b' }}>
                  <strong style={{ color: '#334155' }}>Height:</strong> {player.height}cm
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Game Log */}
        {player.stats && player.stats.length > 0 ? (
          <div className="modern-section-card">
            <h2 className="modern-section-title">Game Log</h2>
            <div className="modern-table-wrapper">
              <table className="modern-table" style={{ minWidth: '950px' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Opponent</th>
                    <th>2PM</th>
                    <th>2PA</th>
                    <th>2P%</th>
                    <th>3PM</th>
                    <th>3PA</th>
                    <th>3P%</th>
                    <th>FTM</th>
                    <th>FTA</th>
                    <th>FT%</th>
                    <th>OREB</th>
                    <th>DREB</th>
                    <th>REB</th>
                    <th>AST</th>
                    <th>TO</th>
                    <th>ST</th>
                    <th>BS</th>
                    <th>PF</th>
                    <th className="highlight-stat">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStats.map((stat) => (
                    <tr key={stat.id}>
                      <td>
                        {stat.match ? (
                          <Link to={`/summer-league/matches/${stat.match.id}`} className="modern-link">
                            {new Date(stat.match.date).toLocaleDateString()}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {stat.match ? (
                          stat.match.team1 === player.team.name ? stat.match.team2 : stat.match.team1
                        ) : 'N/A'}
                      </td>
                      <td>{stat.two_pm || 0}</td>
                      <td>{stat.two_pa || 0}</td>
                      <td>
                        {(stat.two_pa || 0) > 0 ? ((stat.two_pm || 0) / (stat.two_pa || 0) * 100).toFixed(0) + '%' : '0%'}
                      </td>
                      <td>{stat.three_pm || 0}</td>
                      <td>{stat.three_pa || 0}</td>
                      <td>
                        {(stat.three_pa || 0) > 0 ? ((stat.three_pm || 0) / (stat.three_pa || 0) * 100).toFixed(0) + '%' : '0%'}
                      </td>
                      <td>{stat.ftm || 0}</td>
                      <td>{stat.fta || 0}</td>
                      <td>
                        {(stat.fta || 0) > 0 ? ((stat.ftm || 0) / (stat.fta || 0) * 100).toFixed(0) + '%' : '0%'}
                      </td>
                      <td>{stat.oreb || 0}</td>
                      <td>{stat.dreb || 0}</td>
                      <td>{stat.reb || 0}</td>
                      <td>{stat.ast || 0}</td>
                      <td>{stat.to || 0}</td>
                      <td>{stat.st || 0}</td>
                      <td>{stat.bs || 0}</td>
                      <td>{stat.pf || 0}</td>
                      <td className="highlight-stat">{stat.pts || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="modern-section-card">
            <h2 className="modern-section-title">Game Statistics</h2>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '16px', margin: '24px 0' }}>
              No game statistics available for this player.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDetail;